import { useState, FC, PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { Location } from 'history';
import {
  selectIsBrandAffinityFeatureFlagOn,
  selectIsFrequencyOptimizationReportingFlagOn,
  selectIsSendTimeOptimizationInsightsFeatureFlagOn,
} from 'core/userContext';
import { DataManagementProvider } from 'modules/intelligentInsights/brandAffinityInsights/contexts/dataManagement/DataManagementProvider';
import { useCoreContext as useBrandAffinityReportingCoreContext } from 'modules/intelligentInsights/brandAffinityInsights/contexts/core/CoreContext';
import { FixedWidth } from 'ui/utility/fixedWidth';
import { useNavigate } from 'react-router-dom-v5-compat';
import { TabsGroup, NavTabTrigger, TabsContent } from 'ui/tabsGroup';
import { BrandAffinityIndex } from 'modules/intelligentInsights/brandAffinityInsights/components/layout/BrandAffinityIndex';
import {
  StyledNavListBackground,
  StyledTabContentBackground,
  StyledNavTabList,
} from 'modules/intelligentInsights/IntelligentInsights.styles';
import { IntelligentInsightsHeader } from './IntelligentInsightsTitle';
import { CoreContextProvider as BAInsightsCoreContextProvider } from 'modules/intelligentInsights/brandAffinityInsights/contexts/core/CoreProvider';
import { CoreContextProvider as FroInsightsCoreContextProvider } from 'modules/intelligentInsights/frequencyOptimization/contexts/CoreProvider';
import { CoreContextProvider as STOInsightsCoreContextProvider } from 'modules/intelligentInsights/stoInsights/contexts/CoreProvider';
import { FrequencyOptimizationInsightsIndexPage } from 'modules/intelligentInsights/frequencyOptimization/FrequencyOptimizationReportingIndexPage';
import { BrandAffinityNotEnabled } from 'modules/intelligentInsights/brandAffinityInsights/components/layout/BrandAffinityNotEnabled';
import { ManagementSettingsProvider } from 'modules/intelligentInsights/frequencyOptimization/contexts/ManagementSettingsProvider';
import { RumComponentContextProvider } from 'services/observability/datadog';
import { Product, Team } from 'teams';
import { selectEnabledPreviews } from 'modules/app/selectors';
import {
  FRO_PREVIEW_KEY,
  INTELLIGENT_INSIGHTS_DISPLAY_NAME,
  IntelligentInsightsTab,
} from 'modules/intelligentInsights/constants';
import { STOIndex } from './stoInsights/STOIndex';

export const intelligentInsightsTabOrder = [IntelligentInsightsTab.BA, IntelligentInsightsTab.FRO];

const ConnectedBAInsightsDataMgmtProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const {
    fetchCoreData: fetchWithVars,
    insightsData,
    isLoading,
  } = useBrandAffinityReportingCoreContext();

  return (
    <DataManagementProvider
      coreData={insightsData}
      isLoadingData={isLoading}
      fetchCoreData={fetchWithVars}
    >
      {children}
    </DataManagementProvider>
  );
};

export const parseIntelligentInsightsTab = (location: Location) => {
  const path = location.pathname.split('/');
  const intendedPathWithParams = path[path.length - 1];
  const intendedPath = intendedPathWithParams.split('?')[0];
  return (
    Object.values(IntelligentInsightsTab).find((tab) => tab === intendedPath) ||
    IntelligentInsightsTab.BA
  );
};

export const IntelligentInsightsIndexPage = () => {
  const isBrandAffinityEnabled = useSelector(selectIsBrandAffinityFeatureFlagOn);
  const isFroInsightsEnabled = useSelector(selectIsFrequencyOptimizationReportingFlagOn);
  const isStoInsightsEnabled = useSelector(selectIsSendTimeOptimizationInsightsFeatureFlagOn);
  const enabledPreview = useSelector(selectEnabledPreviews);
  const displayFroPreview = enabledPreview.includes(FRO_PREVIEW_KEY);

  const navigate = useNavigate();
  const history = useHistory();
  const location = useLocation();
  const parsedTab = parseIntelligentInsightsTab(location);

  const shouldRedirectToBA =
    parsedTab === IntelligentInsightsTab.FRO && !isFroInsightsEnabled && !displayFroPreview;
  let selectedTab = parsedTab;
  if (shouldRedirectToBA) {
    selectedTab = IntelligentInsightsTab.BA;
  }

  const [selectedIntelligentInsightsTabs, setSelectedIntelligentInsightsTabs] =
    useState(selectedTab);

  if (shouldRedirectToBA) {
    history.replace(`/intelligentInsights/${IntelligentInsightsTab.BA}`);
  }

  const onTabSelect = (tab: IntelligentInsightsTab) => {
    navigate(`/intelligentInsights/${tab}`);
    setSelectedIntelligentInsightsTabs(tab);
  };

  if (!isBrandAffinityEnabled && !isFroInsightsEnabled) {
    return <BrandAffinityNotEnabled />;
  }

  const displayFRO = isFroInsightsEnabled || displayFroPreview;

  return (
    <>
      <IntelligentInsightsHeader />
      <RumComponentContextProvider
        componentName="IntelligentInsights/BrandAffinity"
        productName={Product.BRAND_AFFINITY_INSIGHTS}
        teamName={Team.AI_TEAM}
      >
        <BAInsightsCoreContextProvider>
          <ConnectedBAInsightsDataMgmtProvider>
            <TabsGroup
              value={selectedIntelligentInsightsTabs}
              onValueChange={(newTab) => onTabSelect(newTab as IntelligentInsightsTab)}
            >
              <StyledNavListBackground>
                <FixedWidth>
                  <StyledNavTabList>
                    <NavTabTrigger
                      key={IntelligentInsightsTab.BA}
                      data-test={`${IntelligentInsightsTab.BA}-tab`}
                      value={IntelligentInsightsTab.BA}
                    >
                      {INTELLIGENT_INSIGHTS_DISPLAY_NAME[IntelligentInsightsTab.BA]}
                    </NavTabTrigger>
                    {isStoInsightsEnabled && (
                      <NavTabTrigger
                        key={IntelligentInsightsTab.STO}
                        data-test={`${IntelligentInsightsTab.STO}-tab`}
                        value={IntelligentInsightsTab.STO}
                      >
                        {INTELLIGENT_INSIGHTS_DISPLAY_NAME[IntelligentInsightsTab.STO]}
                      </NavTabTrigger>
                    )}
                    {displayFRO && (
                      <NavTabTrigger
                        key={IntelligentInsightsTab.FRO}
                        data-test={`${IntelligentInsightsTab.FRO}-tab`}
                        value={IntelligentInsightsTab.FRO}
                      >
                        {INTELLIGENT_INSIGHTS_DISPLAY_NAME[IntelligentInsightsTab.FRO]}
                      </NavTabTrigger>
                    )}
                  </StyledNavTabList>
                </FixedWidth>
              </StyledNavListBackground>
              <TabsContent key={IntelligentInsightsTab.BA} value={IntelligentInsightsTab.BA}>
                <StyledTabContentBackground>
                  <FixedWidth>
                    <BrandAffinityIndex />
                  </FixedWidth>
                </StyledTabContentBackground>
              </TabsContent>
              {isStoInsightsEnabled && (
                <STOInsightsCoreContextProvider>
                  <TabsContent key={IntelligentInsightsTab.STO} value={IntelligentInsightsTab.STO}>
                    <StyledTabContentBackground>
                      <FixedWidth>
                        <STOIndex />
                      </FixedWidth>
                    </StyledTabContentBackground>
                  </TabsContent>
                </STOInsightsCoreContextProvider>
              )}
              <RumComponentContextProvider
                componentName="IntelligentInsights/FRO"
                productName={Product.FRO_INSIGHTS}
                teamName={Team.AI_TEAM}
              >
                <FroInsightsCoreContextProvider>
                  <ManagementSettingsProvider>
                    <TabsContent
                      key={IntelligentInsightsTab.FRO}
                      value={IntelligentInsightsTab.FRO}
                    >
                      <StyledTabContentBackground>
                        <FixedWidth>
                          <FrequencyOptimizationInsightsIndexPage />
                        </FixedWidth>
                      </StyledTabContentBackground>
                    </TabsContent>
                  </ManagementSettingsProvider>
                </FroInsightsCoreContextProvider>
              </RumComponentContextProvider>
            </TabsGroup>
          </ConnectedBAInsightsDataMgmtProvider>
        </BAInsightsCoreContextProvider>
      </RumComponentContextProvider>
    </>
  );
};
